import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../auth/useAuthStore';
import { usePortalAccess } from './PortalAccessContext';
import { clientPortalApi } from './api';
import { ProgrammingBridgeState, spliceVisorTemplate } from './spliceVisorTemplate';
import { useProgrammingBridge } from './useProgrammingBridge';

/**
 * Réplica de `detectSameOriginIframe` en `AnalysisViewerPage.tsx` — mismo
 * criterio, dos consumidores del mismo mecanismo de iframe Blob.
 */
function detectSameOriginIframe(iframeWindow: Window | null | undefined): string | null {
  try {
    const origin: string | undefined = iframeWindow?.location.origin;
    if (origin && origin !== 'null' && origin === window.location.origin) {
      return origin;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Pestaña PROGRAMACIÓN de LineOperationsPage — embebe el mismo template
 * legacy que `AnalysisViewerPage`, pero con `isolateModule: 'programacion'`:
 * una hoja de estilo adicional oculta marca/tabs/mapa/inspector y deja
 * visible únicamente el módulo de programación, para que dentro del panel
 * izquierdo de LineOperationsPage se sienta como una pestaña más — no como
 * "otra app" ni un visor-dentro-de-visor. El motor `Eng.*` (bloques,
 * ciclo, flota, frecuencias) sigue siendo exactamente el mismo código sin
 * tocar; solo cambia qué porción del DOM es visible. Solo conecta
 * `useProgrammingBridge` — Cobertura ya no vive en este iframe (pasó a
 * React nativo), así que `useCoverageBridge` no tiene nada que hacer acá.
 */
export function ProgrammingLegacyPane({ analysisId, label }: { analysisId: string; label: string }): JSX.Element {
  const { slug } = usePortalAccess();
  const token = useAuthStore(state => state.token);
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const channelIdRef = useRef<string>('');
  const targetOriginRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    const channelId = crypto.randomUUID();
    channelIdRef.current = channelId;

    Promise.all([
      clientPortalApi.getAnalysisTemplate(slug, analysisId, token),
      clientPortalApi.getAnalysisBundle(slug, analysisId, token),
      clientPortalApi.getAnalysisAudit(slug, analysisId, token),
      clientPortalApi.getProgramming(slug, analysisId, token),
    ])
      .then(([template, bundle, audit, programming]) => {
        if (cancelled) return;
        const programmingState: ProgrammingBridgeState = programming;
        const html = spliceVisorTemplate(template, bundle, audit, programmingState, channelId, { analysisId, lineName: label }, {
          isolateModule: 'programacion',
        });
        objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        setIframeUrl(objectUrl);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return (): void => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [slug, analysisId, token, label]);

  useProgrammingBridge({ slug, analysisId, token, iframeRef, channelIdRef, targetOriginRef });

  function handleIframeLoad(): void {
    targetOriginRef.current = detectSameOriginIframe(iframeRef.current?.contentWindow);
  }

  if (state === 'loading') {
    return <p className="cov-analytics-empty">Cargando programación…</p>;
  }
  if (state === 'error' || !iframeUrl) {
    return <p className="ft-error">No se pudo cargar la programación de esta línea.</p>;
  }

  return (
    <iframe
      ref={iframeRef}
      title={`Programación ${label}`}
      src={iframeUrl}
      onLoad={handleIframeLoad}
      style={{ display: 'block', width: '100%', height: '100%', border: 'none', background: 'transparent' }}
    />
  );
}
